import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";

export interface AvailabilityEditorHandle {
  save: () => Promise<void>;
}

const GET_AVAILABILITY = gql`
  query AvailabilityEditor($saunaId: Int!) {
    saunaById(id: $saunaId) {
      availabilityRulesBySaunaId(orderBy: DAY_OF_WEEK_ASC) {
        nodes { id dayOfWeek startTime endTime }
      }
      availabilityBlocksBySaunaId(orderBy: START_AT_ASC) {
        nodes { id startAt endAt reason }
      }
    }
  }
`;

const CREATE_RULE = gql`
  mutation CreateAvailabilityRule($saunaId: Int!, $dayOfWeek: Int!, $startTime: Time!, $endTime: Time!) {
    createAvailabilityRule(input: { availabilityRule: {
      saunaId: $saunaId, dayOfWeek: $dayOfWeek, startTime: $startTime, endTime: $endTime
    }}) { availabilityRule { id } }
  }
`;
const UPDATE_RULE = gql`
  mutation UpdateAvailabilityRule($id: Int!, $startTime: Time!, $endTime: Time!) {
    updateAvailabilityRuleById(input: { id: $id, availabilityRulePatch: {
      startTime: $startTime, endTime: $endTime
    }}) { availabilityRule { id } }
  }
`;
const DELETE_RULE = gql`
  mutation DeleteAvailabilityRule($id: Int!) {
    deleteAvailabilityRuleById(input: { id: $id }) { availabilityRule { id } }
  }
`;
const CREATE_BLOCK = gql`
  mutation CreateAvailabilityBlock($saunaId: Int!, $startAt: Datetime!, $endAt: Datetime!, $reason: String) {
    createAvailabilityBlock(input: { availabilityBlock: {
      saunaId: $saunaId, startAt: $startAt, endAt: $endAt, reason: $reason
    }}) { availabilityBlock { id } }
  }
`;
const DELETE_BLOCK = gql`
  mutation DeleteAvailabilityBlock($id: Int!) {
    deleteAvailabilityBlockById(input: { id: $id }) { availabilityBlock { id } }
  }
`;

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DEFAULT_TIMES = { startTime: "10:00", endTime: "22:00" };

interface DayState {
  enabled: boolean;
  startTime: string;
  endTime: string;
  existingId: number | null;
}

function buildDayState(nodes: { id: number; dayOfWeek: number; startTime: string; endTime: string }[]): DayState[] {
  const state: DayState[] = DAYS.map(() => ({ enabled: false, existingId: null, ...DEFAULT_TIMES }));
  for (const n of nodes) {
    state[n.dayOfWeek] = {
      enabled: true,
      existingId: n.id,
      startTime: n.startTime.slice(0, 5),
      endTime: n.endTime.slice(0, 5),
    };
  }
  return state;
}

function fmt12(t: string): string {
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const suffix = h < 12 ? "AM" : "PM";
  h = h % 12 || 12;
  return `${h}:${mStr} ${suffix}`;
}

// ── Schedule preview ────────────────────────────────────────────────────────

function SchedulePreview({ days }: { days: DayState[] }) {
  const enabled = days.filter(d => d.enabled);
  if (enabled.length === 0) {
    return <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>No days configured yet.</p>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {days.map((d, dow) => (
        <div key={dow} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
          <span style={{ width: 72, color: d.enabled ? "#111827" : "#d1d5db", fontWeight: d.enabled ? 600 : 400 }}>
            {DAYS[dow].slice(0, 3)}
          </span>
          {d.enabled ? (
            <span style={{
              padding: "2px 10px", borderRadius: 12,
              background: "#dcfce7", color: "#15803d",
              fontWeight: 500,
            }}>
              {fmt12(d.startTime)} – {fmt12(d.endTime)}
            </span>
          ) : (
            <span style={{ color: "#d1d5db" }}>—</span>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

const AvailabilityEditor = forwardRef<AvailabilityEditorHandle, { saunaId: number; hideSaveButton?: boolean }>(
function AvailabilityEditor({ saunaId, hideSaveButton = false }, ref) {
  const { data, loading } = useQuery(GET_AVAILABILITY, { variables: { saunaId } });
  const [createRule] = useMutation(CREATE_RULE, { refetchQueries: ["AvailabilityEditor"] });
  const [updateRule] = useMutation(UPDATE_RULE, { refetchQueries: ["AvailabilityEditor"] });
  const [deleteRule] = useMutation(DELETE_RULE, { refetchQueries: ["AvailabilityEditor"] });
  const [createBlock] = useMutation(CREATE_BLOCK, { refetchQueries: ["AvailabilityEditor"] });
  const [deleteBlock] = useMutation(DELETE_BLOCK, { refetchQueries: ["AvailabilityEditor"] });

  const [days, setDays] = useState<DayState[]>(DAYS.map(() => ({ enabled: false, existingId: null, ...DEFAULT_TIMES })));
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const [blockStart, setBlockStart] = useState("");
  const [blockEnd, setBlockEnd] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [blockError, setBlockError] = useState<string | null>(null);
  const [blocking, setBlocking] = useState(false);

  useEffect(() => {
    const nodes = data?.saunaById?.availabilityRulesBySaunaId?.nodes;
    if (nodes) setDays(buildDayState(nodes));
  }, [data]);

  function setDay(dow: number, patch: Partial<DayState>) {
    setDays(prev => prev.map((d, i) => i === dow ? { ...d, ...patch } : d));
  }

  async function saveSchedule() {
    setSaveMsg(null);
    setSaving(true);
    try {
      const original = buildDayState(data?.saunaById?.availabilityRulesBySaunaId?.nodes ?? []);
      const ops: Promise<any>[] = [];
      for (let dow = 0; dow < 7; dow++) {
        const cur = days[dow];
        const orig = original[dow];
        if (cur.enabled && orig.enabled && orig.existingId !== null) {
          if (cur.startTime !== orig.startTime || cur.endTime !== orig.endTime) {
            ops.push(updateRule({ variables: { id: orig.existingId, startTime: cur.startTime, endTime: cur.endTime } }));
          }
        } else if (cur.enabled && !orig.enabled) {
          ops.push(createRule({ variables: { saunaId, dayOfWeek: dow, startTime: cur.startTime, endTime: cur.endTime } }));
        } else if (!cur.enabled && orig.enabled && orig.existingId !== null) {
          ops.push(deleteRule({ variables: { id: orig.existingId } }));
        }
      }
      const results = await Promise.all(ops);
      const firstErr = results.find(r => r.errors && r.errors.length)?.errors?.[0];
      if (firstErr) throw new Error(firstErr.message);
      setSaveMsg({ ok: true, text: "Schedule saved." });
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (err: any) {
      setSaveMsg({ ok: false, text: err?.message ?? "Failed to save schedule" });
      setSaving(false);
      throw err;
    } finally {
      setSaving(false);
    }
  }

  useImperativeHandle(ref, () => ({ save: saveSchedule }));

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    await saveSchedule();
  }

  async function handleCreateBlock(e: React.FormEvent) {
    e.preventDefault();
    setBlockError(null);
    if (!blockStart || !blockEnd) { setBlockError("Start and end are required"); return; }
    if (new Date(blockEnd) <= new Date(blockStart)) { setBlockError("End must be after start"); return; }
    setBlocking(true);
    try {
      const result = await createBlock({
        variables: {
          saunaId,
          startAt: new Date(blockStart).toISOString(),
          endAt: new Date(blockEnd).toISOString(),
          reason: blockReason || null,
        },
      });
      if (result.errors && result.errors.length) {
        setBlockError(result.errors[0].message);
        return;
      }
      setBlockStart(""); setBlockEnd(""); setBlockReason("");
    } catch (err: any) {
      setBlockError(err?.message ?? "Failed to create block");
    } finally {
      setBlocking(false);
    }
  }

  async function handleDeleteBlock(id: number) {
    if (!window.confirm("Remove this block?")) return;
    try {
      await deleteBlock({ variables: { id } });
    } catch (err: any) {
      alert(err?.message ?? "Failed to remove block");
    }
  }

  if (loading) return <p style={{ fontSize: 14, color: "#9ca3af" }}>Loading availability...</p>;

  const blocks = data?.saunaById?.availabilityBlocksBySaunaId?.nodes ?? [];

  return (
    <div>
      {/* Schedule editor + preview side by side on wider screens */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, alignItems: "start" }}>

        {/* Left: editor */}
        <form onSubmit={handleSave}>
          <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 14px" }}>
            Set which days guests can book and during what hours.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            {DAYS.map((day, dow) => {
              const d = days[dow];
              return (
                <div key={dow} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <label style={{ width: 86, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={d.enabled} onChange={e => setDay(dow, { enabled: e.target.checked })} />
                    <span style={{ fontSize: 13, fontWeight: d.enabled ? 600 : 400 }}>{day}</span>
                  </label>
                  {d.enabled ? (
                    <>
                      <input type="time" value={d.startTime} onChange={e => setDay(dow, { startTime: e.target.value })} style={timeInput} />
                      <span style={{ fontSize: 12, color: "#9ca3af" }}>–</span>
                      <input type="time" value={d.endTime}   onChange={e => setDay(dow, { endTime:   e.target.value })} style={timeInput} />
                    </>
                  ) : (
                    <span style={{ fontSize: 12, color: "#d1d5db" }}>Unavailable</span>
                  )}
                </div>
              );
            })}
          </div>
          {saveMsg && (
            <p style={{ fontSize: 13, color: saveMsg.ok ? "#16a34a" : "#ef4444", margin: "0 0 10px" }}>
              {saveMsg.text}
            </p>
          )}
          {!hideSaveButton && (
            <button type="submit" disabled={saving} style={primaryBtn}>
              {saving ? "Saving..." : "Save schedule"}
            </button>
          )}
        </form>

        {/* Right: live preview */}
        <div style={{ background: "#f9fafb", borderRadius: 8, padding: 16 }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1, margin: "0 0 12px" }}>
            Preview
          </p>
          <SchedulePreview days={days} />
        </div>
      </div>

      <hr style={{ margin: "28px 0", borderColor: "#f3f4f6" }} />

      {/* Date blocks */}
      <p style={{ fontSize: 13, fontWeight: 600, margin: "0 0 4px" }}>Date blocks</p>
      <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 12px" }}>
        Block specific dates — holidays, maintenance, etc.
      </p>

      {blocks.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
          {blocks.map((b: any) => (
            <div key={b.id} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px",
            }}>
              <span style={{ fontSize: 13 }}>
                {new Date(b.startAt).toLocaleDateString()} – {new Date(b.endAt).toLocaleDateString()}
                {b.reason && <span style={{ color: "#6b7280", marginLeft: 8 }}>{b.reason}</span>}
              </span>
              <button onClick={() => handleDeleteBlock(b.id)} style={removeBtn}>Remove</button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleCreateBlock}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, alignItems: "end" }}>
          <div>
            <label style={labelStyle}>Start</label>
            <input type="datetime-local" value={blockStart} onChange={e => setBlockStart(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>End</label>
            <input type="datetime-local" value={blockEnd} onChange={e => setBlockEnd(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Reason (optional)</label>
            <input type="text" value={blockReason} onChange={e => setBlockReason(e.target.value)} placeholder="e.g. Maintenance" style={inputStyle} />
          </div>
          <button type="submit" disabled={blocking} style={{ ...primaryBtn, background: "#374151", whiteSpace: "nowrap" }}>
            {blocking ? "Adding..." : "Add block"}
          </button>
        </div>
        {blockError && <p style={{ fontSize: 13, color: "#ef4444", margin: "6px 0 0" }}>{blockError}</p>}
      </form>
    </div>
  );
});

export default AvailabilityEditor;

const timeInput: React.CSSProperties  = { padding: "5px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 12 };
const labelStyle: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 600, marginBottom: 3, color: "#6b7280" };
const inputStyle: React.CSSProperties = { width: "100%", padding: "7px 9px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 12, boxSizing: "border-box" };
const primaryBtn: React.CSSProperties = { padding: "8px 20px", background: "#2563EB", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 };
const removeBtn: React.CSSProperties  = { background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 12 };
